import { and, eq, inArray } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useStore } from '@tinycld/core/lib/pocketbase'
import { useMyLiveQuery } from '@tinycld/core/lib/use-my-live-query'
import { useMemo } from 'react'
import { mimeTypeToCategory } from '../components/file-icons'
import type { DriveItemView, FolderTreeNode, SidebarSection } from '../types'
import type { DriveSearchResult } from './useDriveSearch'

interface UseDriveItemsParams {
    userId: string
    activeSection: SidebarSection
    currentFolderId: string
    selectedItemId: string | null
    previewItemId: string | null
    searchQuery: string
    searchResults: DriveSearchResult[]
    isSearchActive: boolean
}

/**
 * Loads the drive_items needed for the current view via on-demand server-side
 * filters, then assembles them into the shapes the UI consumes (currentItems,
 * folderTree, breadcrumbs, etc.).
 *
 * The drive_items collection runs on-demand with per-query realtime — each
 * useLiveQuery here translates its where/orderBy into a PocketBase filter, so
 * we never load the whole org's items just to show one folder, and a row that
 * leaves a query's filter is dropped via the server's delete event for that
 * subscription.
 *
 *   - `currentFolderQuery`  — the items shown in the main pane.
 *   - `foldersQuery`        — every folder the user can see; small and stable;
 *                             drives the sidebar tree, breadcrumbs, and the
 *                             folder section above the file list.
 *   - `sectionQuery`        — only fires for non-my-drive sections.
 *   - `selectedItemQuery`   — fires when a selection / preview id isn't in
 *                             any of the above subsets (e.g. shared link).
 *
 * drive_shares and drive_item_state are also on-demand now, but their queries
 * here are unfiltered (or filtered only by the current user), so they still
 * load and behave like a small, always-subscribed set.
 */
export function useDriveItems({
    userId,
    activeSection,
    currentFolderId,
    selectedItemId,
    previewItemId,
    searchResults,
    isSearchActive,
}: UseDriveItemsParams) {
    const [itemsCollection] = useStore('drive_items')
    const [sharesCollection] = useStore('drive_shares')
    const [stateCollection] = useStore('drive_item_state')
    const [usersCollection] = useStore('users')

    // --- supporting collections (small, read unfiltered) ----------------------

    const { data: rawShares, isLoading: sharesLoading } = useLiveQuery(query =>
        query.from({ share: sharesCollection })
    )

    const { data: rawStates, isLoading: statesLoading } = useMyLiveQuery((query, { userId }) =>
        query.from({ state: stateCollection }).where(({ state }) => eq(state.user, userId))
    )

    // All users in the single database are members, and drive_items.created_by
    // can name any of them, so this stays unfiltered by role/id. pbtsdb
    // doesn't pass a fields= param to PocketBase, so .select() here narrows
    // the row shape userNames/userEmails/orgMembers depend on — it does not
    // shrink what comes over the wire.
    const { data: allUsers, isLoading: usersLoading } = useLiveQuery(query =>
        query.from({ user: usersCollection }).select(({ user }) => ({
            id: user.id,
            name: user.name,
            email: user.email,
            avatar: user.avatar,
            avatar_crop: user.avatar_crop,
            avatar_color: user.avatar_color,
            avatar_emoji: user.avatar_emoji,
        }))
    )

    const userNames = useMemo(
        () => new Map((allUsers ?? []).map(u => [u.id, u.name || u.email || ''])),
        [allUsers]
    )

    // Share-picker candidates, separately scoped from allUsers: an item's
    // owner/created_by can be any user (userNames/userEmails must resolve
    // them regardless of role), but a share can only be offered to a
    // non-guest, non-disabled member.
    const { data: memberCandidates } = useLiveQuery(query =>
        query
            .from({ user: usersCollection })
            .where(({ user }) =>
                and(inArray(user.role, ['owner', 'admin', 'member']), eq(user.disabled, false))
            )
            .select(({ user }) => ({ id: user.id, name: user.name, email: user.email }))
    )

    const orgMembers = useMemo(
        () =>
            (memberCandidates ?? [])
                .filter(u => u.id !== userId)
                .map(u => ({
                    userId: u.id,
                    name: u.name || '',
                    email: u.email || '',
                })),
        [memberCandidates, userId]
    )

    const userEmails = useMemo(
        () => new Map((allUsers ?? []).map(u => [u.id, u.email || ''])),
        [allUsers]
    )

    const sharesByItem = useMemo(() => {
        const map = new Map<string, NonNullable<typeof rawShares>>()
        for (const share of rawShares ?? []) {
            const list = map.get(share.item) ?? []
            list.push(share)
            map.set(share.item, list)
        }
        return map
    }, [rawShares])

    const stateByItem = useMemo(() => new Map((rawStates ?? []).map(s => [s.item, s])), [rawStates])

    // --- on-demand drive_items queries ----------------------------------------
    // Each live query against drive_items is translated to a PocketBase filter
    // and run server-side, with realtime scoped to that query. Disabled
    // queries return empty data.

    // Items in the current folder for the current org. Only meaningful when the
    // user is inside My Drive (or a subfolder); other sections supply their own
    // listing via sectionQuery below.
    const showCurrentFolder = !isSearchActive && activeSection === 'my-drive'
    const { data: rawCurrentFolderItems, isLoading: currentFolderLoading } = useLiveQuery({
        query: query => {
            if (!showCurrentFolder) return null
            return query
                .from({ item: itemsCollection })
                .where(({ item }) => eq(item.parent, currentFolderId))
        },
    })

    // Every folder the user can see in this org. Small set (folders are a tiny
    // fraction of items), drives the sidebar tree, breadcrumb resolution, and
    // the folder section above the file list.
    const { data: rawFolders, isLoading: foldersLoading } = useLiveQuery(query =>
        query.from({ item: itemsCollection }).where(({ item }) => eq(item.is_folder, true))
    )

    // Section-specific listings — only fired for sections other than my-drive,
    // which has its own currentFolderQuery.
    const isStarredSection = activeSection === 'starred'
    const isTrashSection = activeSection === 'trash'
    const isRecentSection = activeSection === 'recent'
    const isSharedSection = activeSection === 'shared-with-me'
    const sectionScoped =
        !isSearchActive &&
        (isStarredSection || isTrashSection || isRecentSection || isSharedSection)

    // Collect the set of item ids that should appear in the active id-driven
    // section (starred/trash via drive_item_state, shared-with-me via the user's
    // non-owner drive_shares rows). Avoids ne/not in the server filter — pbtsdb's
    // converter emits `!(...)`, which PocketBase's filter parser doesn't accept.
    const idDrivenSectionIds = useMemo(() => {
        if (isStarredSection) {
            return (rawStates ?? []).filter(s => s.is_starred && !s.trashed_at).map(s => s.item)
        }
        if (isTrashSection) {
            return (rawStates ?? []).filter(s => !!s.trashed_at).map(s => s.item)
        }
        if (isSharedSection) {
            return (rawShares ?? [])
                .filter(s => s.user === userId && s.role !== 'owner')
                .map(s => s.item)
        }
        return null
    }, [isStarredSection, isTrashSection, isSharedSection, rawStates, rawShares, userId])

    const { data: rawSectionItems, isLoading: sectionLoading } = useLiveQuery({
        query: query => {
            if (!sectionScoped) return null
            const base = query.from({ item: itemsCollection })
            if (isRecentSection) {
                return base
                    .where(({ item }) => eq(item.is_folder, false))
                    .orderBy(({ item }) => item.updated, 'desc')
                    .limit(20)
            }
            // starred / trash / shared-with-me are id-driven via the membership
            // rows we already have locally. If the user has no rows in this set,
            // skip the fetch entirely.
            if (!idDrivenSectionIds || idDrivenSectionIds.length === 0) return null
            return base.where(({ item }) => inArray(item.id, idDrivenSectionIds))
        },
    })

    // Selected/preview lookup. If the URL-driven id isn't in any of the loaded
    // subsets, fetch it directly so the preview / selection still resolves.
    const wantedLookupId = previewItemId ?? selectedItemId ?? null
    const lookupAlreadyLoaded = useMemo(() => {
        if (!wantedLookupId) return true
        const matches = (rows: { id: string }[] | undefined) =>
            !!rows && rows.some(it => it.id === wantedLookupId)
        return matches(rawCurrentFolderItems) || matches(rawFolders) || matches(rawSectionItems)
    }, [wantedLookupId, rawCurrentFolderItems, rawFolders, rawSectionItems])

    const lookupNeeded = !!wantedLookupId && !lookupAlreadyLoaded
    const { data: rawLookupItems } = useLiveQuery({
        query: query => {
            if (!lookupNeeded || !wantedLookupId) return null
            return query
                .from({ item: itemsCollection })
                .where(({ item }) => eq(item.id, wantedLookupId))
        },
    })

    // --- merging + view shapes ------------------------------------------------

    // Union of every drive_item we currently have loaded, deduplicated by id.
    // Downstream code can keep using one map regardless of which query supplied
    // each row.
    const loadedItems = useMemo(() => {
        type Item = NonNullable<typeof rawFolders>[number]
        const merged = new Map<string, Item>()
        const sources = [rawCurrentFolderItems, rawFolders, rawSectionItems, rawLookupItems]
        for (const src of sources) {
            if (!src) continue
            for (const row of src) merged.set(row.id, row)
        }
        return [...merged.values()]
    }, [rawCurrentFolderItems, rawFolders, rawSectionItems, rawLookupItems])

    const allItems = useMemo<DriveItemView[]>(
        () =>
            loadedItems.map(item => {
                const state = stateByItem.get(item.id)
                const shares = sharesByItem.get(item.id) ?? []
                const hasNonOwnerShares = shares.some(s => s.role !== 'owner')
                const ownerName = userNames.get(item.created_by) ?? ''

                return {
                    id: item.id,
                    name: item.name,
                    isFolder: item.is_folder,
                    mimeType: item.mime_type,
                    parentId: item.parent ?? '',
                    owner: item.created_by === userId ? 'me' : ownerName,
                    ownerUserId: item.created_by,
                    updated: item.updated,
                    size: item.size,
                    shared: hasNonOwnerShares,
                    starred: state?.is_starred ?? false,
                    trashedAt: state?.trashed_at ?? '',
                    file: item.file,
                    thumbnail: (item as unknown as { thumbnail?: string }).thumbnail ?? '',
                    description: item.description,
                    category: mimeTypeToCategory(item.mime_type, item.is_folder),
                }
            }),
        [loadedItems, stateByItem, sharesByItem, userId, userNames]
    )

    const itemsById = useMemo(() => new Map(allItems.map(i => [i.id, i])), [allItems])

    const searchItemViews = useMemo<DriveItemView[]>(() => {
        if (!isSearchActive) return []
        return searchResults.map(sr => {
            const existing = itemsById.get(sr.id)
            if (existing) return existing
            return {
                id: sr.id,
                name: sr.name,
                isFolder: sr.is_folder,
                mimeType: sr.mime_type,
                parentId: '',
                owner: '',
                ownerUserId: '',
                updated: sr.updated,
                size: sr.size,
                shared: false,
                starred: false,
                trashedAt: '',
                file: '',
                thumbnail: '',
                description: sr.description,
                category: mimeTypeToCategory(sr.mime_type, sr.is_folder),
            }
        })
    }, [isSearchActive, searchResults, itemsById])

    const currentFolderItems = useMemo<DriveItemView[]>(() => {
        if (!showCurrentFolder) return []
        const ids = new Set((rawCurrentFolderItems ?? []).map(i => i.id))
        return allItems.filter(i => ids.has(i.id) && !i.trashedAt)
    }, [showCurrentFolder, rawCurrentFolderItems, allItems])

    const sectionItems = useMemo<DriveItemView[]>(() => {
        if (!sectionScoped) return []
        const ids = new Set((rawSectionItems ?? []).map(i => i.id))
        const filtered = allItems.filter(i => ids.has(i.id))
        // section-level filters that aren't expressible in PB filter syntax,
        // or that depend on local drive_item_state we already have.
        if (isStarredSection) return filtered.filter(i => i.starred && !i.trashedAt)
        if (isTrashSection) return filtered.filter(i => !!i.trashedAt)
        if (isRecentSection) return filtered.filter(i => !i.trashedAt)
        if (isSharedSection) return filtered.filter(i => !i.trashedAt)
        return filtered
    }, [
        sectionScoped,
        rawSectionItems,
        allItems,
        isStarredSection,
        isTrashSection,
        isRecentSection,
        isSharedSection,
    ])

    // currentItems intentionally excludes upload placeholders. The screen
    // composes those in via useUploadPlaceholders so that progress ticks
    // don't churn DriveContextValue.
    const currentItems = useMemo(() => {
        if (isSearchActive) return searchItemViews
        if (showCurrentFolder) return currentFolderItems
        return sectionItems
    }, [isSearchActive, searchItemViews, showCurrentFolder, currentFolderItems, sectionItems])

    const breadcrumbs = useMemo(() => {
        const crumbs: DriveItemView[] = []
        let id = currentFolderId
        while (id) {
            const item = itemsById.get(id)
            if (!item) break
            crumbs.unshift(item)
            id = item.parentId
        }
        return crumbs
    }, [currentFolderId, itemsById])

    const selectedItem = useMemo(
        () => (selectedItemId ? itemsById.get(selectedItemId) : undefined),
        [selectedItemId, itemsById]
    )

    const previewItem = useMemo(
        () => (previewItemId ? (itemsById.get(previewItemId) ?? null) : null),
        [previewItemId, itemsById]
    )

    const folderTree = useMemo(() => {
        const folders = allItems.filter(i => i.isFolder && i.ownerUserId === userId && !i.trashedAt)

        function buildTree(parentId: string): FolderTreeNode[] {
            return folders
                .filter(f => f.parentId === parentId)
                .map(folder => ({
                    item: folder,
                    children: buildTree(folder.id),
                }))
        }

        return buildTree('')
    }, [allItems, userId])

    const isLoading =
        currentFolderLoading ||
        foldersLoading ||
        sectionLoading ||
        sharesLoading ||
        statesLoading ||
        usersLoading

    return {
        itemsById,
        currentItems,
        breadcrumbs,
        selectedItem,
        previewItem,
        folderTree,
        isLoading,
        orgMembers,
        stateByItem,
        sharesByItem,
        userNames,
        userEmails,
    }
}
